
 // =========================================================
// LA ESQUINA DEL PARQUÉS
// HERO CONTROLADO POR DJANGO
// VERIFICACIÓN DE EDAD · FECHA DE NACIMIENTO · TÉRMINOS
// MODO DE MANTENIMIENTO
// =========================================================

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import "../styles/hero.css";

import logoDefault from "../assets/logo.png";
import heroBackgroundDefault from "../assets/bg.png";

// =========================================================
// API
// =========================================================
const API_BASE_URL = (
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD
    ? "https://api.laesquinadelparques.com/api"
    : "http://localhost:8000/api")
).replace(/\/+$/, "");

const HERO_SETTINGS_URL = `${API_BASE_URL}/hero/settings/`;

// =========================================================
// ALMACENAMIENTO DE VERIFICACIÓN DE EDAD
// =========================================================

const AGE_VERIFIED_STORAGE_KEY =
  "la_esquina_del_parques_age_verified_v1";

// =========================================================
// CONFIGURACIÓN PREDETERMINADA
// =========================================================

const DEFAULT_HERO = {
  // GENERAL
  name: "Hero Principal La Esquina del Parqués",
  is_active: true,

  // MANTENIMIENTO
  maintenance_mode: false,
  maintenance_allow_admins: true,
  maintenance_allow_staff: false,

  maintenance_title: "ESTAMOS MEJORANDO",
  maintenance_subtitle: "LA ESQUINA DEL PARQUÉS",
  maintenance_description:
    "Estamos realizando actualizaciones para ofrecerte una mejor experiencia.",
  maintenance_status: "SISTEMA EN ACTUALIZACIÓN",
  maintenance_button_text: "VOLVER A INTENTAR",
  maintenance_button_enabled: true,
  maintenance_footer: "LA ESQUINA DEL PARQUÉS · 2026",
  maintenance_background: "",
  maintenance_logo: "",
  maintenance_primary_color: "#f4b400",
  maintenance_secondary_color: "#087f5b",
  maintenance_accent_color: "#f07818",
  maintenance_show_logo: true,
  maintenance_show_status: true,
  maintenance_show_footer: true,

  // IMÁGENES
  background: "",
  logo: "",

  // BARRA SUPERIOR
  location_text: "MEDELLÍN · COLOMBIA",
  top_title: "LA ESQUINA DEL PARQUÉS",
  rating: 5,
  show_rating: true,
  show_location: true,

  // MARCA
  brand_label: "SABOR · TRADICIÓN · BUENOS MOMENTOS",
  year_first: "20",
  year_second: "26",
  edition_stamp: "LA ESQUINA\nDEL PARQUÉS",

  // INTRODUCCIÓN
  intro_title: "Bienvenidos a La Esquina del Parqués",
  intro_description:
    "Tu punto de encuentro para compartir, disfrutar y vivir una experiencia diferente.\nTradición, sabor y buenos momentos en un solo lugar.",
  show_intro: true,

  // TIENDA
  store_label: "BIENVENIDO",
  store_number: "01",
  store_title: "LA ESQUINA",
  store_title_strong: "DEL PARQUÉS",
  store_description: "TU LUGAR FAVORITO",
  catalog_button_text: "EXPLORAR EL CATÁLOGO",
  catalog_button_arrow: "→",

  // ZONA GRÁFICA
  product_ring_top: "01",
  product_ring_right: "+",
  product_ring_bottom: "026",
  product_ring_left: "+",
  product_left_number: "01",
  product_left_label_1: "CALIDAD",
  product_left_label_2: "Y SERVICIO",
  product_right_number: "2026",
  product_right_label_1: "SABOR",
  product_right_label_2: "Y TRADICIÓN",
  product_mark_small: "LA ESQUINA",
  product_mark_main: "EP",
  product_mark_bottom: "PARQUÉS",
  show_product_zone: true,

  // INFORMACIÓN INFERIOR
  meta_latitude: "",
  meta_longitude: "",
  meta_issue: "NUESTRA ESQUINA",
  meta_edition: "EDICIÓN 2026",
  show_meta: true,

  // BOTÓN SIGUIENTE
  next_button_text: "→",
  next_button_label: "Explorar catálogo",
  show_next_button: true,

  // VERIFICACIÓN DE EDAD
  age_verification_enabled: true,
  minimum_age: 18,

  // PASO 1
  age_step_1_number: "18+",
  age_step_1_label: "VERIFICACIÓN DE EDAD",
  age_step_1_title: "¿ERES MAYOR DE EDAD?",
  age_step_1_description:
    "Confirma que eres mayor de edad para continuar.",
  age_exit_button: "NO, SALIR",
  age_continue_button: "SÍ, SOY MAYOR",
  age_continue_arrow: "→",

  // PASO 2
  age_step_2_number: "02",
  age_step_2_label: "FECHA DE NACIMIENTO",
  age_step_2_title: "CONFIRMA TU FECHA DE NACIMIENTO",
  age_step_2_description:
    "Ingresa tu fecha de nacimiento para verificar que cumples con la edad mínima.",

  // FECHA
  birth_day_label: "DÍA",
  birth_month_label: "MES",
  birth_year_label: "AÑO",
  birth_day_placeholder: "DD",
  birth_month_placeholder: "MM",
  birth_year_placeholder: "AAAA",

  // MENSAJES
  age_valid_message: "EDAD VERIFICADA · {age} AÑOS",
  age_invalid_message: "NO CUMPLES CON LA EDAD MÍNIMA",
  invalid_birth_date_message:
    "Ingresa una fecha de nacimiento válida.",
  underage_message:
    "Debes ser mayor de edad para continuar.",
  terms_required_message:
    "Debes aceptar los términos y condiciones.",
  privacy_required_message:
    "Debes aceptar la política de privacidad.",

  // TÉRMINOS
  terms_checkbox_text: "Acepto los",
  terms_link_text: "términos y condiciones",
  terms_url: "/terminos",

  // PRIVACIDAD
  privacy_checkbox_text: "Acepto la",
  privacy_link_text: "política de privacidad",
  privacy_url: "/privacidad",

  // BOTONES DE EDAD
  age_back_button: "← VOLVER",
  age_catalog_button: "ENTRAR AL CATÁLOGO",
  age_catalog_arrow: "→",
  age_modal_footer: "LA ESQUINA DEL PARQUÉS · 2026",

  // COLORES
  primary_color: "#f4b400",
  secondary_color: "#087f5b",
  accent_color: "#f07818",
  modal_color: "#f4b400",
};

// =========================================================
// HELPERS
// =========================================================

function getAssetUrl(value, fallback) {
  if (!value || typeof value !== "string") {
    return fallback;
  }

  const cleanValue = value.trim();

  if (!cleanValue) {
    return fallback;
  }

  // URL absoluta
  if (/^(https?:\/\/|data:|blob:)/i.test(cleanValue)) {
    return cleanValue;
  }

  // URL relativa desde el dominio de la API
  if (cleanValue.startsWith("/")) {
    try {
      const apiUrl = new URL(API_BASE_URL);
      return `${apiUrl.origin}${cleanValue}`;
    } catch {
      return fallback;
    }
  }

  // Archivo ubicado en MEDIA de Django
  try {
    const apiUrl = new URL(API_BASE_URL);
    const mediaPath =
      `/media/${cleanValue.replace(/^\/+/, "")}`;

    return `${apiUrl.origin}${mediaPath}`;
  } catch {
    return fallback;
  }
}

function replaceAgePlaceholder(text, age) {
  if (!text) {
    return "";
  }

  return String(text).replace("{age}", String(age));
}

function splitText(text) {
  if (!text) {
    return null;
  }

  const lines = String(text).split("\n");

  return lines.map((line, index) => (
    <span key={`${index}-${line}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ));
}

function mergeHeroSettings(backendHero) {
  const merged = { ...DEFAULT_HERO };

  Object.keys(DEFAULT_HERO).forEach((key) => {
    const value = backendHero?.[key];

    if (value !== null && value !== undefined && value !== "") {
      merged[key] = value;
    }
  });

  return merged;
}

function hasAgeVerification() {
  try {
    return (
      localStorage.getItem(AGE_VERIFIED_STORAGE_KEY) === "true"
    );
  } catch {
    return false;
  }
}

function saveAgeVerification() {
  try {
    localStorage.setItem(AGE_VERIFIED_STORAGE_KEY, "true");
    return true;
  } catch {
    return false;
  }
}

// =========================================================
// COMPONENTE HERO
// =========================================================

export default function Hero() {
  const navigate = useNavigate();

  // CONFIGURACIÓN DEL BACKEND
  const [hero, setHero] = useState(DEFAULT_HERO);

  // INTRODUCCIÓN
  const [openIntro, setOpenIntro] = useState(false);

  // MODAL DE EDAD
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [ageStep, setAgeStep] = useState(1);

  // FECHA DE NACIMIENTO
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");

  // ACEPTACIONES
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // ERRORES
  const [ageError, setAgeError] = useState("");

  // =======================================================
  // CARGAR CONFIGURACIÓN DESDE DJANGO
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function loadHero() {
      try {
        const response = await fetch(HERO_SETTINGS_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Error de API Hero: ${response.status}`);
        }

        const data = await response.json();

        const backendHero =
          data?.data?.settings ??
          data?.data ??
          data?.result?.settings ??
          data?.result ??
          data?.settings ??
          data;

        if (
          !backendHero ||
          typeof backendHero !== "object" ||
          Array.isArray(backendHero)
        ) {
          throw new Error("La respuesta del Hero no es válida.");
        }

        if (!cancelled) {
          setHero(mergeHeroSettings(backendHero));
        }
      } catch (error) {
        console.warn(
          "[LA ESQUINA DEL PARQUÉS] No fue posible cargar HeroSettings:",
          error
        );
      }
    }

    loadHero();

    return () => {
      cancelled = true;
    };
  }, []);

  // =======================================================
  // IMÁGENES
  // =======================================================

  const heroBackground = getAssetUrl(
    hero.background,
    heroBackgroundDefault
  );

  const heroLogo = getAssetUrl(
    hero.logo,
    logoDefault
  );

  const maintenanceBackground = getAssetUrl(
    hero.maintenance_background,
    heroBackground
  );

  const maintenanceLogo = getAssetUrl(
    hero.maintenance_logo,
    heroLogo
  );

  // =======================================================
  // VARIABLES CSS
  // =======================================================

  const heroStyle = {
    "--hero-primary":
      hero.primary_color || DEFAULT_HERO.primary_color,
    "--hero-secondary":
      hero.secondary_color || DEFAULT_HERO.secondary_color,
    "--hero-accent":
      hero.accent_color || DEFAULT_HERO.accent_color,
    "--hero-modal":
      hero.modal_color || DEFAULT_HERO.modal_color,
  };

  const maintenanceStyle = {
    "--maintenance-primary":
      hero.maintenance_primary_color ||
      DEFAULT_HERO.maintenance_primary_color,
    "--maintenance-secondary":
      hero.maintenance_secondary_color ||
      DEFAULT_HERO.maintenance_secondary_color,
    "--maintenance-accent":
      hero.maintenance_accent_color ||
      DEFAULT_HERO.maintenance_accent_color,
  };

  // =======================================================
  // ABRIR CATÁLOGO
  // =======================================================

  const handleCatalogClick = (event) => {
    event?.preventDefault();

    if (hero.age_verification_enabled === false) {
      navigate("/catalog");
      return;
    }

    if (hasAgeVerification()) {
      navigate("/catalog");
      return;
    }

    setAgeStep(1);
    setAgeError("");
    setBirthDay("");
    setBirthMonth("");
    setBirthYear("");
    setAcceptedTerms(false);
    setAcceptedPrivacy(false);
    setShowAgeModal(true);
  };

  // =======================================================
  // CALCULAR EDAD
  // =======================================================

  const calculatedAge = useMemo(() => {
    if (!birthDay || !birthMonth || !birthYear) {
      return null;
    }

    const day = Number(birthDay);
    const month = Number(birthMonth);
    const year = Number(birthYear);

    if (!day || !month || !year) {
      return null;
    }

    const birthDate = new Date(year, month - 1, day);

    if (Number.isNaN(birthDate.getTime())) {
      return null;
    }

    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDifference =
      today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (
        monthDifference === 0 &&
        today.getDate() < birthDate.getDate()
      )
    ) {
      age--;
    }

    return age;
  }, [birthDay, birthMonth, birthYear]);

  // =======================================================
  // VALIDAR FECHA
  // =======================================================

  const isValidBirthDate = useMemo(() => {
    if (!birthDay || !birthMonth || !birthYear) {
      return false;
    }

    const day = Number(birthDay);
    const month = Number(birthMonth);
    const year = Number(birthYear);
    const currentYear = new Date().getFullYear();

    if (
      day < 1 ||
      day > 31 ||
      month < 1 ||
      month > 12 ||
      year < 1900 ||
      year > currentYear
    ) {
      return false;
    }

    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    return date <= today;
  }, [birthDay, birthMonth, birthYear]);

  // =======================================================
  // EDAD MÍNIMA Y PERMISOS
  // =======================================================

  const minimumAge = Number(
    hero.minimum_age || DEFAULT_HERO.minimum_age
  );

  const isAdult =
    isValidBirthDate &&
    calculatedAge !== null &&
    calculatedAge >= minimumAge;

  const canEnterCatalog =
    isAdult &&
    acceptedTerms &&
    acceptedPrivacy;

  // =======================================================
  // PASAR AL PASO 2
  // =======================================================

  const continueFromAgeQuestion = () => {
    setAgeError("");
    setAgeStep(2);
  };

  // =======================================================
  // ENTRAR AL CATÁLOGO
  // =======================================================

  const continueToCatalog = () => {
    setAgeError("");

    if (!isValidBirthDate) {
      setAgeError(
        hero.invalid_birth_date_message ||
        DEFAULT_HERO.invalid_birth_date_message
      );
      return;
    }

    if (!isAdult) {
      setAgeError(
        hero.underage_message ||
        DEFAULT_HERO.underage_message
      );
      return;
    }

    if (!acceptedTerms) {
      setAgeError(
        hero.terms_required_message ||
        DEFAULT_HERO.terms_required_message
      );
      return;
    }

    if (!acceptedPrivacy) {
      setAgeError(
        hero.privacy_required_message ||
        DEFAULT_HERO.privacy_required_message
      );
      return;
    }

    saveAgeVerification();
    setShowAgeModal(false);
    setAgeError("");
    navigate("/catalog");
  };

  // =======================================================
  // SALIR DE LA VERIFICACIÓN
  // =======================================================

  const goLogin = () => {
    setShowAgeModal(false);
    setAgeError("");
    navigate("/login");
  };

  // =======================================================
  // MODO MANTENIMIENTO
  // =======================================================

  if (hero.maintenance_mode === true) {
    return (
      <main
        className="hero maintenance-mode"
        style={{
          ...maintenanceStyle,
          backgroundImage: `url(${maintenanceBackground})`,
        }}
      >
        <div
          className="hero-background"
          style={{
            backgroundImage: `url(${maintenanceBackground})`,
          }}
          aria-hidden="true"
        />

        <div className="hero-purple-light" />
        <div className="hero-pink-light" />
        <div className="hero-orange-light" />
        <div className="hero-horizon" />
        <div className="hero-grid" />
        <div className="hero-noise" />
        <div className="hero-scanlines" />
        <div className="hero-vignette" />

        <header className="hero-topbar">
          <div className="hero-location">
            <span className="hero-location-dot" />
            <span>{hero.location_text}</span>
          </div>

          <div className="hero-top-title">
            {hero.maintenance_subtitle}
          </div>

          <div className="hero-rating" aria-hidden="true">
            {hero.show_rating &&
              Array.from({
                length: Math.min(Number(hero.rating) || 5, 5),
              }).map((_, index) => (
                <span key={index}>★</span>
              ))}
          </div>
        </header>

        <section className="hero-poster maintenance-poster">
          <div className="hero-brand-block">
            {hero.maintenance_show_logo && (
              <div className="hero-brand">
                <img
                  src={maintenanceLogo}
                  alt={hero.top_title}
                  className="hero-logo"
                  style={{
                    width: "min(360px, 34vw)",
                    maxWidth: "360px",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            <div className="hero-brand-label">
              {hero.maintenance_subtitle}
            </div>
          </div>

          <div className="hero-intro hero-intro--active">
            <div className="hero-intro-line" />
            <h1>{hero.maintenance_title}</h1>
            <p>{splitText(hero.maintenance_description)}</p>
            <div className="hero-intro-line" />
          </div>

          <div className="hero-store-card maintenance-card">
            <div className="hero-store-card-header">
              <span>MANTENIMIENTO</span>
              <span>01</span>
            </div>

            <div className="hero-store-title">
              {hero.maintenance_subtitle}
            </div>

            {hero.maintenance_show_status && (
              <div className="hero-store-description">
                {hero.maintenance_status}
              </div>
            )}

            {hero.maintenance_button_enabled && (
              <button
                type="button"
                className="hero-catalog-button"
                onClick={() => window.location.reload()}
                style={{
                  width: "min(430px, 100%)",
                  maxWidth: "100%",
                  minHeight: "48px",
                  padding: "10px 18px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
              >
                <span>{hero.maintenance_button_text}</span>
                <span className="hero-button-arrow">→</span>
              </button>
            )}
          </div>

          <div className="hero-product-zone">
            <div className="hero-product-ring">
              <span className="ring-marker ring-marker-top">01</span>
              <span className="ring-marker ring-marker-right">+</span>
              <span className="ring-marker ring-marker-bottom">026</span>
              <span className="ring-marker ring-marker-left">+</span>
            </div>

            <div className="hero-product-glow" />

            <div className="hero-product-mark">
              <div className="hero-product-mark-inner">
                <span className="hero-product-mark-small">
                  LA ESQUINA
                </span>
                <span className="hero-product-mark-main">EP</span>
                <span className="hero-product-mark-bottom">
                  PARQUÉS
                </span>
              </div>
            </div>
          </div>

          {hero.maintenance_show_footer && (
            <div className="hero-meta">
              <span>{hero.maintenance_footer}</span>
            </div>
          )}
        </section>
      </main>
    );
  }

  // =======================================================
  // HERO PRINCIPAL
  // =======================================================

  return (
    <>
      <main className="hero" style={heroStyle}>
        <div
          className="hero-background"
          style={{
            backgroundImage: `url(${heroBackground})`,
          }}
          aria-hidden="true"
        />

        <div className="hero-purple-light" />
        <div className="hero-pink-light" />
        <div className="hero-orange-light" />
        <div className="hero-horizon" />
        <div className="hero-grid" />

        <div className="hero-palm hero-palm-left">
          <span>♣</span>
        </div>

        <div className="hero-palm hero-palm-right">
          <span>♣</span>
        </div>

        <div className="hero-noise" />
        <div className="hero-scanlines" />
        <div className="hero-vignette" />

        <header className="hero-topbar">
          {hero.show_location && (
            <div className="hero-location">
              <span className="hero-location-dot" />
              <span>{hero.location_text}</span>
            </div>
          )}

          <div className="hero-top-title">
            {hero.top_title}
          </div>

          {hero.show_rating && (
            <div
              className="hero-rating"
              aria-label={`${hero.rating} estrellas`}
            >
              {Array.from({
                length: Math.min(Number(hero.rating) || 5, 5),
              }).map((_, index) => (
                <span key={index}>★</span>
              ))}
            </div>
          )}
        </header>

        <section className="hero-poster">
          <div className="hero-brand-block">
            <div className="hero-brand-label">
              {hero.brand_label}
            </div>

            <div className="hero-brand">
              <button
                type="button"
                className="hero-logo-button"
                onClick={() => setOpenIntro(!openIntro)}
                aria-label={`Mostrar información de ${hero.top_title}`}
              >
                <img
                  src={heroLogo}
                  alt={hero.top_title}
                  className="hero-logo"
                  style={{
                    width: "min(360px, 34vw)",
                    maxWidth: "360px",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </button>
            </div>

            <div className="hero-year" aria-hidden="true">
              <span>{hero.year_first}</span>
              <span>{hero.year_second}</span>
            </div>

            <div className="hero-edition-stamp">
              {splitText(hero.edition_stamp)}
            </div>
          </div>

          {hero.show_intro && (
            <div
              className={`hero-intro ${
                openIntro ? "hero-intro--active" : ""
              }`}
            >
              <div className="hero-intro-line" />
              <h1>{hero.intro_title}</h1>
              <p>{splitText(hero.intro_description)}</p>
              <div className="hero-intro-line" />
            </div>
          )}

          <div className="hero-store-card">
            <div className="hero-store-card-header">
              <span>{hero.store_label}</span>
              <span>{hero.store_number}</span>
            </div>

            <div className="hero-store-title">
              {hero.store_title}
              <strong>{hero.store_title_strong}</strong>
            </div>

            <div className="hero-store-description">
              {hero.store_description}
            </div>

            <button
              type="button"
              className="hero-catalog-button"
              onClick={handleCatalogClick}
              style={{
                width: "min(430px, 100%)",
                maxWidth: "100%",
                minHeight: "48px",
                padding: "10px 18px",
                fontSize: "16px",
                boxSizing: "border-box",
              }}
            >
              <span>{hero.catalog_button_text}</span>
              <span className="hero-button-arrow">
                {hero.catalog_button_arrow}
              </span>
            </button>
          </div>

          {hero.show_product_zone && (
            <div className="hero-product-zone">
              <div className="hero-product-ring">
                <span className="ring-marker ring-marker-top">
                  {hero.product_ring_top}
                </span>
                <span className="ring-marker ring-marker-right">
                  {hero.product_ring_right}
                </span>
                <span className="ring-marker ring-marker-bottom">
                  {hero.product_ring_bottom}
                </span>
                <span className="ring-marker ring-marker-left">
                  {hero.product_ring_left}
                </span>
              </div>

              <div className="hero-product-glow" />

              <div className="hero-product-copy hero-product-copy-left">
                <span>{hero.product_left_number}</span>
                <small>{hero.product_left_label_1}</small>
                <small>{hero.product_left_label_2}</small>
              </div>

              <div className="hero-product-copy hero-product-copy-right">
                <span>{hero.product_right_number}</span>
                <small>{hero.product_right_label_1}</small>
                <small>{hero.product_right_label_2}</small>
              </div>

              <div className="hero-product-shadow" />

              <div className="hero-product-mark">
                <div className="hero-product-mark-inner">
                  <span className="hero-product-mark-small">
                    {hero.product_mark_small}
                  </span>
                  <span className="hero-product-mark-main">
                    {hero.product_mark_main}
                  </span>
                  <span className="hero-product-mark-bottom">
                    {hero.product_mark_bottom}
                  </span>
                </div>
              </div>
            </div>
          )}

          {hero.show_meta && (
            <div className="hero-meta">
              {hero.meta_latitude && (
                <span>{hero.meta_latitude}</span>
              )}
              {hero.meta_longitude && (
                <span>{hero.meta_longitude}</span>
              )}
              <span>{hero.meta_issue}</span>
              <span>{hero.meta_edition}</span>
            </div>
          )}

          {hero.show_next_button && (
            <button
              type="button"
              className="hero-next"
              onClick={handleCatalogClick}
              aria-label={hero.next_button_label}
            >
              <span>{hero.next_button_text}</span>
            </button>
          )}
        </section>
      </main>

      {/* =====================================================
          MODAL DE VERIFICACIÓN DE EDAD
      ===================================================== */}

      {showAgeModal && (
        <div
          className="age-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="age-modal-title"
        >
          <div className="age-modal">
            <div
              className="age-modal-colorbar"
              style={{ background: hero.modal_color }}
            />

            {/* PASO 1 */}
            {ageStep === 1 && (
              <>
                <div className="age-modal-number">
                  {hero.age_step_1_number}
                </div>

                <div className="age-modal-label">
                  {hero.age_step_1_label}
                </div>

                <div className="age-modal-line" />

                <h2 id="age-modal-title">
                  {splitText(hero.age_step_1_title)}
                </h2>

                <p>{hero.age_step_1_description}</p>

                <div className="age-buttons">
                  <button
                    type="button"
                    className="btn-register"
                    onClick={goLogin}
                  >
                    {hero.age_exit_button}
                  </button>

                  <button
                    type="button"
                    className="btn-continue"
                    onClick={continueFromAgeQuestion}
                  >
                    <span>{hero.age_continue_button}</span>
                    <strong>{hero.age_continue_arrow}</strong>
                  </button>
                </div>

                <div className="age-modal-footer">
                  {hero.age_modal_footer}
                </div>
              </>
            )}

            {/* PASO 2 */}
            {ageStep === 2 && (
              <>
                <div className="age-modal-number">
                  {hero.age_step_2_number}
                </div>

                <div className="age-modal-label">
                  {hero.age_step_2_label}
                </div>

                <div className="age-modal-line" />

                <h2 id="age-modal-title">
                  {splitText(hero.age_step_2_title)}
                </h2>

                <p>{hero.age_step_2_description}</p>

                <div className="birth-date-fields">
                  <div className="birth-date-field">
                    <label htmlFor="birth-day">
                      {hero.birth_day_label}
                    </label>

                    <input
                      id="birth-day"
                      type="number"
                      min="1"
                      max="31"
                      placeholder={hero.birth_day_placeholder}
                      value={birthDay}
                      onChange={(event) => {
                        setBirthDay(event.target.value);
                        setAgeError("");
                      }}
                    />
                  </div>

                  <div className="birth-date-field">
                    <label htmlFor="birth-month">
                      {hero.birth_month_label}
                    </label>

                    <input
                      id="birth-month"
                      type="number"
                      min="1"
                      max="12"
                      placeholder={hero.birth_month_placeholder}
                      value={birthMonth}
                      onChange={(event) => {
                        setBirthMonth(event.target.value);
                        setAgeError("");
                      }}
                    />
                  </div>

                  <div className="birth-date-field birth-date-year">
                    <label htmlFor="birth-year">
                      {hero.birth_year_label}
                    </label>

                    <input
                      id="birth-year"
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      placeholder={hero.birth_year_placeholder}
                      value={birthYear}
                      onChange={(event) => {
                        setBirthYear(event.target.value);
                        setAgeError("");
                      }}
                    />
                  </div>
                </div>

                {calculatedAge !== null && isValidBirthDate && (
                  <div
                    className={
                      isAdult
                        ? "age-result age-result-valid"
                        : "age-result age-result-invalid"
                    }
                  >
                    {isAdult
                      ? replaceAgePlaceholder(
                          hero.age_valid_message,
                          calculatedAge
                        )
                      : hero.age_invalid_message}
                  </div>
                )}

                <div className="age-legal-options">
                  <label className="age-checkbox">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(event) =>
                        setAcceptedTerms(event.target.checked)
                      }
                    />

                    <span className="age-checkbox-box" />

                    <span className="age-checkbox-text">
                      {hero.terms_checkbox_text}{" "}

                      <button
                        type="button"
                        className="age-inline-link"
                        onClick={(event) => {
                          event.preventDefault();

                          if (hero.terms_url) {
                            window.open(
                              hero.terms_url,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                      >
                        {hero.terms_link_text}
                      </button>
                    </span>
                  </label>

                  <label className="age-checkbox">
                    <input
                      type="checkbox"
                      checked={acceptedPrivacy}
                      onChange={(event) =>
                        setAcceptedPrivacy(event.target.checked)
                      }
                    />

                    <span className="age-checkbox-box" />

                    <span className="age-checkbox-text">
                      {hero.privacy_checkbox_text}{" "}

                      <button
                        type="button"
                        className="age-inline-link"
                        onClick={(event) => {
                          event.preventDefault();

                          if (hero.privacy_url) {
                            window.open(
                              hero.privacy_url,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                      >
                        {hero.privacy_link_text}
                      </button>
                    </span>
                  </label>
                </div>

                {ageError && (
                  <div className="age-error" role="alert">
                    {ageError}
                  </div>
                )}

                <div className="age-buttons age-buttons-step-two">
                  <button
                    type="button"
                    className="btn-register"
                    onClick={() => {
                      setAgeStep(1);
                      setAgeError("");
                    }}
                  >
                    {hero.age_back_button}
                  </button>

                  <button
                    type="button"
                    className="btn-continue"
                    disabled={!canEnterCatalog}
                    onClick={continueToCatalog}
                  >
                    <span>{hero.age_catalog_button}</span>
                    <strong>{hero.age_catalog_arrow}</strong>
                  </button>
                </div>

                <div className="age-modal-footer">
                  {hero.age_modal_footer}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
